/**
 * Utility to fetch cities for a given country via API route
 * with client-side caching for performance
 */

export interface City {
  id: string;
  label: string;
  population?: number;
  adminName?: string; // State/Province/Region
}

// In-memory cache to store fetched cities per country
const citiesCache: Map<string, City[]> = new Map();

/**
 * Fetches major cities for a given country via our API route
 * Results are cached to avoid repeated API calls
 */
export async function fetchCitiesForCountry(
  countryName: string
): Promise<City[]> {
  // Return cached cities if available
  if (citiesCache.has(countryName)) {
    return citiesCache.get(countryName)!;
  }

  try {
    // Call our API route which handles Google Places API securely
    const response = await fetch(
      `/api/cities?country=${encodeURIComponent(countryName)}`
    );

    if (!response.ok) {
      console.error('Failed to fetch cities:', response.statusText);
      return [];
    }

    const data = await response.json();

    if (data.error) {
      console.error('Error from cities API:', data.error);
      return [];
    }

    const cities: City[] = data.cities || [];

    // Cache the results (API already caches, but this avoids network calls)
    citiesCache.set(countryName, cities);

    return cities;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
}

/**
 * Clear the cities cache (useful for testing or memory management)
 */
export function clearCitiesCache() {
  citiesCache.clear();
}

/**
 * Get cached cities for a country without making an API call
 */
export function getCachedCities(countryName: string): City[] | null {
  return citiesCache.get(countryName) || null;
}
